<script setup lang="ts">
  import { Copy, Download, Trash } from "lucide-vue-next";
  import { IndexTable, IndexTableName, IndexTableDate } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import { Badge } from "@/components/ui/badge";
  import type { MasterReportListItem } from "@/types";

  defineProps<{
    masterReports: MasterReportListItem[];
    loading: boolean;
  }>();

  const emit = defineEmits<{
    open: [masterReport: MasterReportListItem, event: MouseEvent | KeyboardEvent];
    download: [masterReport: MasterReportListItem];
    duplicate: [masterReport: MasterReportListItem];
    delete: [masterReport: MasterReportListItem];
  }>();

  const columns: DataTableColumn<MasterReportListItem>[] = [
    { key: "name", label: "Master report" },
    { key: "status", label: "Status", width: 200 },
    { key: "date", label: "Date", width: 90 },
  ];

  const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;
  const subtitle = (report: MasterReportListItem) =>
    `${plural(report.slide_templates_count, "slide")} · ${plural(report.chart_templates_count, "chart")}`;

  const actionsFor = (report: MasterReportListItem) => [
    { isDelete: false, function: () => emit("download", report), text: "download PPTX", icon: Download, condition: !!report.has_pptx_file },
    { isDelete: false, function: () => emit("duplicate", report), text: "duplicate", icon: Copy, condition: !report.is_system },
    { isDelete: false, function: () => emit("delete", report), text: "delete", icon: Trash, condition: !report.is_system },
  ];
</script>

<template>
  <IndexTable
    :items="masterReports"
    :columns="columns"
    :loading="loading"
    actions-class="w-[60px]"
    :skeleton-rows="6"
    @row-click="(report, event) => emit('open', report, event)">
    <template #cell-name="{ item }">
      <IndexTableName
        :name="item.name"
        :subtitle="subtitle(item)" />
    </template>
    <template #cell-status="{ item }">
      <div class="flex items-center gap-1.5">
        <Badge
          :variant="item.active ? 'success' : 'neutral'"
          class="text-2xs font-semibold">
          {{ item.active ? "Active" : "Inactive" }}
        </Badge>
        <Badge
          v-if="item.is_qualitative"
          variant="purple"
          class="text-2xs">
          Qualitative
        </Badge>
      </div>
    </template>
    <template #cell-date="{ item }">
      <IndexTableDate :date="item.created_at" />
    </template>
    <template #actions="{ item }">
      <div
        class="flex justify-end"
        @click.stop>
        <ActionsDropdown
          :actions="actionsFor(item)"
          :token="item.token" />
      </div>
    </template>
    <template #empty>
      <slot name="empty" />
    </template>
  </IndexTable>
</template>
